import { z } from "zod";

export const menuThemeKeys = [
  "mono-editorial",
  "modern-minimal",
  "warm-bistro",
  "luxury-dark",
  "rustic-kraft",
  "mediterranean",
  "neon-street",
  "pastel-cafe",
  "elegant-serif",
  "bold-color-block",
  "fresh-organic",
  "classic-diner",
  "modern-arabesque",
] as const;

export const menuThemeKeySchema = z.enum(menuThemeKeys);
export type MenuThemeKey = z.infer<typeof menuThemeKeySchema>;

export const menuLanguageSchema = z.enum(["ar", "en"]);
export type MenuLanguage = z.infer<typeof menuLanguageSchema>;

export const menuSlugSchema = z
  .string()
  .trim()
  .min(3)
  .max(80)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "Use lowercase letters, numbers, and single hyphens");

export const reservedMenuSlugs = new Set([
  "admin",
  "api",
  "client",
  "controller",
  "downloads",
  "login",
  "login-admin",
  "menu",
  "player",
  "reset-password",
  "unavailable",
]);

export function normalizeMenuSlug(value: string): string {
  return value
    .normalize("NFKD")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .replace(/-{2,}/g, "-");
}

const uuidSchema = z.string().uuid();
const optionalText = (maximum: number) => z.string().trim().max(maximum).default("");
const requiredDraftText = (maximum: number) => z.string().max(maximum).default("");
const optionalHttpUrl = z
  .union([
    z.literal(""),
    z
      .url()
      .max(2_000)
      .refine((value) => /^https?:\/\//i.test(value), "Only HTTP(S) URLs are supported")
      .refine((value) => {
        const parsed = new URL(value);
        return !parsed.username && !parsed.password;
      }, "URLs cannot contain credentials"),
  ])
  .default("");

export const menuPriceSchema = z
  .string()
  .trim()
  .regex(/^(?:0|[1-9]\d{0,6})(?:\.\d{1,2})?$/, "Use a nonnegative price with at most 2 decimals");

export const menuCurrencySchema = z
  .string()
  .trim()
  .toUpperCase()
  .regex(/^[A-Z]{3}$/, "Use a three-letter ISO currency code")
  .default("QAR");

export const menuLocalizedTextSchema = z
  .object({
    ar: requiredDraftText(2_000),
    en: requiredDraftText(2_000),
  })
  .strict();

export const menuOpeningHoursSchema = z
  .object({
    day: z.enum(["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"]),
    closed: z.boolean().default(false),
    opensAt: z
      .string()
      .regex(/^$|^(?:[01]\d|2[0-3]):[0-5]\d$/)
      .default(""),
    closesAt: z
      .string()
      .regex(/^$|^(?:[01]\d|2[0-3]):[0-5]\d$/)
      .default(""),
  })
  .strict()
  .superRefine((value, context) => {
    if (!value.closed && (!value.opensAt || !value.closesAt))
      context.addIssue({ code: "custom", message: "Open days require opening and closing times" });
  });

export const menuSocialLinkSchema = z
  .object({
    id: uuidSchema,
    network: z.enum(["instagram", "facebook", "tiktok", "x", "youtube", "linkedin"]),
    url: optionalHttpUrl,
  })
  .strict();

export const menuSliderEntrySchema = z
  .object({
    id: uuidSchema,
    assetId: uuidSchema,
    alt: menuLocalizedTextSchema,
    caption: menuLocalizedTextSchema,
  })
  .strict();
export type MenuSliderEntry = z.infer<typeof menuSliderEntrySchema>;

export const menuVariantSchema = z
  .object({
    id: uuidSchema,
    name: menuLocalizedTextSchema,
    price: menuPriceSchema,
  })
  .strict();
export type MenuVariant = z.infer<typeof menuVariantSchema>;

export const menuItemSchema = z
  .object({
    id: uuidSchema,
    name: menuLocalizedTextSchema,
    description: menuLocalizedTextSchema,
    imageAssetId: uuidSchema.nullable().default(null),
    price: menuPriceSchema,
    visible: z.boolean().default(true),
    variants: z.array(menuVariantSchema).max(30).default([]),
  })
  .strict();
export type MenuItem = z.infer<typeof menuItemSchema>;

export const menuCategorySchema = z
  .object({
    id: uuidSchema,
    name: menuLocalizedTextSchema,
    description: menuLocalizedTextSchema,
    coverAssetId: uuidSchema.nullable().default(null),
    visible: z.boolean().default(true),
    slides: z.array(menuSliderEntrySchema).max(20).default([]),
    items: z.array(menuItemSchema).max(500).default([]),
  })
  .strict();
export type MenuCategory = z.infer<typeof menuCategorySchema>;

export const menuDraftSchema = z
  .object({
    profile: z
      .object({
        name: menuLocalizedTextSchema,
        description: menuLocalizedTextSchema,
        logoAssetId: uuidSchema.nullable().default(null),
        coverAssetId: uuidSchema.nullable().default(null),
        defaultLanguage: menuLanguageSchema.default("ar"),
        currency: menuCurrencySchema,
        phone: optionalText(40),
        whatsapp: optionalText(500),
        address: menuLocalizedTextSchema,
        mapUrl: optionalHttpUrl,
        websiteUrl: optionalHttpUrl,
        openingHours: z.array(menuOpeningHoursSchema).length(7),
        socialLinks: z.array(menuSocialLinkSchema).max(12).default([]),
        themeKey: menuThemeKeySchema.default("mono-editorial"),
      })
      .strict(),
    homeSlides: z.array(menuSliderEntrySchema).max(20).default([]),
    categories: z.array(menuCategorySchema).max(100).default([]),
  })
  .strict()
  .superRefine((value, context) => {
    const ids = [
      ...value.homeSlides.map((entry) => entry.id),
      ...value.categories.flatMap((category) => [
        category.id,
        ...category.slides.map((entry) => entry.id),
        ...category.items.flatMap((item) => [
          item.id,
          ...item.variants.map((variant) => variant.id),
        ]),
      ]),
    ];
    if (new Set(ids).size !== ids.length)
      context.addIssue({ code: "custom", message: "Menu record identifiers must be unique" });
    const days = value.profile.openingHours.map((entry) => entry.day);
    if (new Set(days).size !== 7)
      context.addIssue({
        code: "custom",
        path: ["profile", "openingHours"],
        message: "Opening hours must include every day exactly once",
      });
  });

export type MenuDraft = z.infer<typeof menuDraftSchema>;

export function emptyMenuDraft(): MenuDraft {
  const days = [
    "sunday",
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
  ] as const;
  return menuDraftSchema.parse({
    profile: {
      name: { ar: "", en: "" },
      description: { ar: "", en: "" },
      address: { ar: "", en: "" },
      openingHours: days.map((day) => ({ day, closed: true, opensAt: "", closesAt: "" })),
    },
  });
}

export const menuDraftUpdateSchema = z
  .object({
    revision: z.number().int().min(0),
    slug: menuSlugSchema,
    draft: menuDraftSchema,
  })
  .strict();

export const menuTranslationRequestSchema = z
  .object({
    sourceLanguage: menuLanguageSchema,
    targetLanguage: menuLanguageSchema,
    sourceText: z.string().trim().min(1).max(4_000),
    currentTargetText: z.string().max(4_000).default(""),
    draftRevision: z.number().int().min(0),
  })
  .strict()
  .refine((value) => value.sourceLanguage !== value.targetLanguage, {
    message: "Source and target languages must differ",
    path: ["targetLanguage"],
  });

export const menuTranslationOutputSchema = z
  .object({ translation: z.string().trim().min(1).max(4_000) })
  .strict();

export function menuAssetIds(draft: MenuDraft): string[] {
  const ids = [
    draft.profile.logoAssetId,
    draft.profile.coverAssetId,
    ...draft.homeSlides.map((entry) => entry.assetId),
    ...draft.categories.flatMap((category) => [
      category.coverAssetId,
      ...category.slides.map((entry) => entry.assetId),
      ...category.items.map((item) => item.imageAssetId),
    ]),
  ];
  return [...new Set(ids.filter((id): id is string => Boolean(id)))];
}

export function menuPublishIssues(draft: MenuDraft): string[] {
  const issues: string[] = [];
  if (!draft.profile.name.ar.trim()) issues.push("Arabic restaurant name is required");
  if (!draft.profile.name.en.trim()) issues.push("English restaurant name is required");
  if (!draft.categories.some((category) => category.visible))
    issues.push("At least one visible category is required");
  draft.categories.forEach((category, categoryIndex) => {
    if (!category.visible) return;
    if (!category.name.ar.trim()) issues.push(`Category ${categoryIndex + 1} needs an Arabic name`);
    if (!category.name.en.trim())
      issues.push(`Category ${categoryIndex + 1} needs an English name`);
    category.items.forEach((item, itemIndex) => {
      if (!item.visible) return;
      if (!item.name.ar.trim())
        issues.push(`Item ${itemIndex + 1} in category ${categoryIndex + 1} needs an Arabic name`);
      if (!item.name.en.trim())
        issues.push(`Item ${itemIndex + 1} in category ${categoryIndex + 1} needs an English name`);
      item.variants.forEach((variant, variantIndex) => {
        if (!variant.name.ar.trim())
          issues.push(`Variant ${variantIndex + 1} for item ${itemIndex + 1} needs an Arabic name`);
        if (!variant.name.en.trim())
          issues.push(
            `Variant ${variantIndex + 1} for item ${itemIndex + 1} needs an English name`,
          );
      });
    });
  });
  return issues;
}
